import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Maximize2, Minus, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import JanelaDestacada, {
  BotaoDestacar,
} from '@core/layout/janela-destacada/JanelaDestacada'
import { coordFrameFor, coordFrameLabel } from '@viewer/measure/coordReadout'
import { isIdentityOrientation } from './sectionMath'
import InterruptorDoPreenchimento from './InterruptorDoPreenchimento'
// Duas casas e sem "-0.00" num zero arredondado (a cota de um modelo
// que começa a 0 vinha -0.00).
import { fmtNum } from '@viewer/views2d/format'
import {
  aplicarValorApresentado,
  eixosApresentados,
  intervaloApresentado,
} from './eixosSectionBox'
import {
  gravarJanela,
  lerJanela,
  limitarPosicao,
  posicaoPorOmissao,
  type Ponto,
  type Tamanho,
} from './janelaSectionBox'

/** Movimento, em px, abaixo do qual largar a barra minimizada conta
 *  como clique (restaura) e não como arrasto. */
const CLIQUE_PX = 4

/** Painel da Section box: janela flutuante dentro da área do 3D.
 *
 *  - arrasta-se pela barra do título e nunca sai da área do 3D;
 *  - minimiza-se para uma barra ("Section box · ligada");
 *  - o "×" fecha só o painel: a caixa continua a cortar, e o botão
 *    "Section box" da Vista reabre-o;
 *  - "Desligar" desliga a caixa.
 *
 *  - destaca-se para uma janela do browser (segundo ecrã); fechar essa
 *    janela, ou "Voltar ao painel", trá-lo de volta para cima do 3D;
 *    desligar a caixa fecha-a.
 *
 *  Não se reutiliza o `FloatingPanel` do BCF nem as janelas de `popups/`:
 *  são `fixed` no ecrã inteiro e não minimizam. Esta janela tem de ficar
 *  presa ao 3D (está no mesmo contentor do cubo de navegação). */
function SectionBoxPanel() {
  const enabled = useAppStore((s) => s.sectionEnabled)
  const orientation = useAppStore((s) => s.sectionOrientation)
  const extents = useAppStore((s) => s.sectionExtents)
  const range = useAppStore((s) => s.sectionExtentRange)
  const setExtents = useAppStore((s) => s.setSectionExtents)
  const setEnabled = useAppStore((s) => s.setSectionEnabled)
  const reset = useAppStore((s) => s.resetSection)
  const alignMode = useAppStore((s) => s.sectionAlignMode)
  const setAlignMode = useAppStore((s) => s.setSectionAlignMode)
  const painel = useAppStore((s) => s.sectionPanel)
  const setPainel = useAppStore((s) => s.setSectionPanel)
  const mostrarCaixa = useAppStore((s) => s.sectionMostrarCaixa)
  const setMostrarCaixa = useAppStore((s) => s.setSectionMostrarCaixa)
  const destacado = useAppStore((s) => s.sectionPanelDestacado)
  const setDestacado = useAppStore((s) => s.setSectionPanelDestacado)
  // Estáveis: a janela destacada guarda-os e não pode reabrir por causa
  // de uma identidade nova.
  const voltarAoPainel = useCallback(() => setDestacado(false), [setDestacado])
  const fecharDestacado = useCallback(() => {
    setDestacado(false)
    setPainel('fechado')
  }, [setDestacado, setPainel])
  const frame = useAppStore((s) =>
    coordFrameFor(
      s.models.map((m) => m.id),
      s.geoBaselines,
      s.geoOverrides,
    ),
  )

  const janelaRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<Ponto | null>(() => lerJanela().pos)
  const posRef = useRef<Ponto | null>(pos)
  useEffect(() => {
    posRef.current = pos
  })
  const arrasto = useRef<{
    inicio: Ponto
    pos0: Ponto
    moveu: boolean
    aoLargarSemMover?: () => void
  } | null>(null)

  const aVista = enabled && !!orientation && !!extents && !!range && painel !== 'fechado'
  // Destacado, o painel vive na janela do browser e a moldura dentro do
  // 3D não existe (não se mede nem se arrasta).
  const visivel = aVista && !destacado
  const { z, trazer } = useJanelaAberta('section-box', visivel, 'canvas')
  const minimizado = painel === 'minimizado'

  /** Área do 3D (o contentor posicionado onde a janela vive) e o tamanho
   *  da própria janela. */
  const medir = useCallback((): { area: Tamanho; janela: Tamanho } | null => {
    const el = janelaRef.current
    const cont = el?.offsetParent ?? el?.parentElement
    if (!el || !cont) return null
    const a = cont.getBoundingClientRect()
    const j = el.getBoundingClientRect()
    return { area: { w: a.width, h: a.height }, janela: { w: j.width, h: j.height } }
  }, [])

  // Primeira abertura sem posição guardada: por baixo do cubo. Com
  // posição guardada (ou ao minimizar/restaurar, que muda o tamanho),
  // volta a caber na área.
  useLayoutEffect(() => {
    if (!visivel) return
    const m = medir()
    if (!m) return
    const alvo = posRef.current ?? posicaoPorOmissao(m.area, m.janela)
    const limitado = limitarPosicao(alvo, m.janela, m.area)
    if (!posRef.current || limitado.x !== posRef.current.x || limitado.y !== posRef.current.y) {
      setPos(limitado)
    }
  }, [visivel, minimizado, medir])

  // A área do 3D muda de tamanho (janela do browser, painéis laterais):
  // a janela volta a ficar lá dentro.
  useEffect(() => {
    if (!visivel) return
    const cont = janelaRef.current?.offsetParent ?? janelaRef.current?.parentElement
    const reenquadrar = () => {
      const m = medir()
      const p = posRef.current
      if (!m || !p) return
      const l = limitarPosicao(p, m.janela, m.area)
      if (l.x !== p.x || l.y !== p.y) setPos(l)
    }
    window.addEventListener('resize', reenquadrar)
    let ro: ResizeObserver | null = null
    if (cont && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(reenquadrar)
      ro.observe(cont)
    }
    return () => {
      window.removeEventListener('resize', reenquadrar)
      ro?.disconnect()
    }
  }, [visivel, medir])

  // Memória por browser: posição e minimizado.
  useEffect(() => {
    if (painel === 'fechado') return
    gravarJanela({ pos, minimizado: painel === 'minimizado' })
  }, [pos, painel])

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = arrasto.current
      if (!d) return
      const dx = e.clientX - d.inicio.x
      const dy = e.clientY - d.inicio.y
      if (!d.moveu && Math.hypot(dx, dy) < CLIQUE_PX) return
      d.moveu = true
      const m = medir()
      const alvo = { x: d.pos0.x + dx, y: d.pos0.y + dy }
      setPos(m ? limitarPosicao(alvo, m.janela, m.area) : alvo)
    }
    const onUp = () => {
      const d = arrasto.current
      arrasto.current = null
      if (d && !d.moveu) d.aoLargarSemMover?.()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [medir])

  const comecarArrasto = (e: React.PointerEvent, aoLargarSemMover?: () => void) => {
    if (e.button !== 0) return
    // Os botões da barra fazem a sua acção; não arrastam.
    if ((e.target as Element).closest?.('button,input')) return
    e.preventDefault()
    arrasto.current = {
      inicio: { x: e.clientX, y: e.clientY },
      pos0: posRef.current ?? { x: 0, y: 0 },
      moveu: false,
      aoLargarSemMover,
    }
  }

  if (!aVista || !orientation || !extents || !range) return null

  const isIdentity = isIdentityOrientation(orientation)
  const eixos = eixosApresentados(frame, isIdentity)
  const estilo = pos
    ? { left: pos.x, top: pos.y, zIndex: z }
    : // Antes da primeira medição: invisível no canto, para medir o
      // tamanho sem piscar por cima do cubo.
      { left: 0, top: 0, zIndex: z, visibility: 'hidden' as const }

  const botao =
    'rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'

  const corpo = (
    <div className="p-3">
      <div className="mb-2 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => setAlignMode(!alignMode)}
          aria-pressed={alignMode}
          title="Alinhar à face: depois clica numa superfície do modelo"
          className={`rounded border px-2 py-0.5 text-[10px] transition-colors ${
            alignMode
              ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }`}
        >
          {alignMode ? 'Clica numa face…' : 'Alinhar à face'}
        </button>
        <button
          type="button"
          onClick={reset}
          title="Repor para os limites do modelo (alinhada aos eixos)"
          className={botao}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={() => setEnabled(false)}
          title="Desligar a section box (deixa de cortar o modelo)"
          className={`${botao} ml-auto`}
        >
          Desligar
        </button>
      </div>

      <label
        className="mb-2 flex cursor-pointer items-center gap-1.5 text-[10px] text-[var(--color-text-muted)]"
        title="Esconder as arestas, as faces e as pegas da caixa. O corte mantém-se e as pegas deixam de apanhar cliques."
      >
        <input
          type="checkbox"
          checked={mostrarCaixa}
          onChange={(e) => setMostrarCaixa(e.target.checked)}
          data-testid="section-box-mostrar-caixa"
          className="accent-[var(--color-accent)]"
        />
        Mostrar caixa
        {!mostrarCaixa && (
          <span className="text-[var(--color-text-dim)]">
            (escondida; o corte mantém-se)
          </span>
        )}
      </label>

      <InterruptorDoPreenchimento />

      <div
        className="mb-2 text-[10px] text-[var(--color-text-muted)]"
        data-testid="section-box-referencial"
      >
        {isIdentity ? (
          <>
            Eixos alinhados com as coordenadas {coordFrameLabel(frame)}:{' '}
            {eixos.map((e) => e.rotulo).join(', ')} (Z para cima, como no IFC).
          </>
        ) : (
          <>
            Caixa rodada (alinhada à face). Eixos locais: X e Y são paralelos
            à face, Z é a normal.
          </>
        )}
      </div>

      {eixos.map((eixo) => {
        const lim = intervaloApresentado(range, eixo)
        const val = intervaloApresentado(extents, eixo)
        const mudar = (lado: 'min' | 'max', v: number) =>
          setExtents(aplicarValorApresentado(extents, eixo, lado, v))
        return (
          <div
            key={eixo.interno}
            data-testid={`section-box-eixo-${eixo.rotulo}`}
            className="mb-2 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40 p-2 last:mb-0"
          >
            <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              <span>
                {isIdentity ? `Eixo ${eixo.rotulo}` : eixo.rotulo}
                {eixo.nota && (
                  <span className="ml-1 text-[var(--color-accent)]">({eixo.nota})</span>
                )}
              </span>
              <span className="font-mono">
                {fmtNum(lim.min)} … {fmtNum(lim.max)}
              </span>
            </div>
            <AxisSlider
              rotulo={eixo.rotulo}
              side="min"
              value={val.min}
              min={lim.min}
              max={lim.max}
              onChange={(v) => mudar('min', v)}
            />
            <AxisSlider
              rotulo={eixo.rotulo}
              side="max"
              value={val.max}
              min={lim.min}
              max={lim.max}
              onChange={(v) => mudar('max', v)}
            />
          </div>
        )
      })}
    </div>
  )

  if (destacado) {
    return (
      <JanelaDestacada
        aberta
        nome="vn2r-section-box"
        titulo="Section box"
        features="width=400,height=620,resizable=yes,scrollbars=yes"
        oQue="a Section box"
        aoVoltar={voltarAoPainel}
        aoFechar={fecharDestacado}
      >
        <div data-testid="section-box-destacada" className="text-xs">
          {corpo}
        </div>
      </JanelaDestacada>
    )
  }

  if (minimizado) {
    return (
      <div
        ref={janelaRef}
        role="dialog"
        aria-label="Section box (minimizada)"
        data-testid="section-box-janela"
        data-minimizada="true"
        style={estilo}
        onPointerDownCapture={trazer}
        onPointerDown={(e) => comecarArrasto(e, () => setPainel('aberto'))}
        title="Arrasta para mudar de sítio; clica para restaurar"
        className="pointer-events-auto absolute flex cursor-move select-none items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs shadow-lg"
      >
        <span className="font-medium text-[var(--color-text)]">
          Section box · ligada{mostrarCaixa ? '' : ' (caixa escondida)'}
        </span>
        <button
          type="button"
          onClick={() => setPainel('aberto')}
          aria-label="Restaurar o painel da section box"
          title="Restaurar"
          className="rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <Maximize2 size={11} />
        </button>
      </div>
    )
  }

  return (
    <div
      ref={janelaRef}
      role="dialog"
      aria-label="Section box"
      data-testid="section-box-janela"
      data-minimizada="false"
      style={estilo}
      onPointerDownCapture={trazer}
      className="pointer-events-auto absolute w-[320px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-xs shadow-lg"
    >
      <div
        data-testid="section-box-titulo"
        onPointerDown={(e) => comecarArrasto(e)}
        title="Arrasta para mudar o painel de sítio"
        className="flex cursor-move select-none items-center justify-between gap-2 border-b border-[var(--color-border)] px-3 py-1.5"
      >
        <span className="font-medium text-[var(--color-text)]">Section box</span>
        <div className="flex items-center gap-1">
          <BotaoDestacar
            onClick={() => setDestacado(true)}
            rotulo="Destacar a section box para uma janela do browser"
          />
          <button
            type="button"
            onClick={() => setPainel('minimizado')}
            aria-label="Minimizar o painel da section box"
            title="Minimizar (a caixa continua ligada)"
            className="rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <Minus size={12} />
          </button>
          <button
            type="button"
            onClick={() => setPainel('fechado')}
            aria-label="Fechar o painel da section box"
            title="Fechar o painel. A caixa continua ligada; o botão Section box da Vista reabre o painel."
            className="rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {corpo}
    </div>
  )
}

function AxisSlider({
  rotulo,
  side,
  value,
  min,
  max,
  onChange,
}: {
  rotulo: string
  side: 'min' | 'max'
  value: number
  min: number
  max: number
  onChange: (v: number) => void
}) {
  const span = Math.max(0.0001, max - min)
  const step = span / 1000
  return (
    <div className="mb-1 flex items-center gap-2 last:mb-0">
      <span className="w-8 text-[10px] text-[var(--color-text-muted)]">{side}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.min(max, Math.max(min, value))}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 accent-[var(--color-accent)]"
        aria-label={`${rotulo} ${side}`}
      />
      <input
        type="number"
        value={Number(value.toFixed(3))}
        step={step}
        aria-label={`${rotulo} ${side} (valor)`}
        onChange={(e) => {
          const v = parseFloat(e.target.value)
          if (!Number.isFinite(v)) return
          onChange(v)
        }}
        className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-right font-mono text-[10px] text-[var(--color-text)]"
      />
    </div>
  )
}

export default SectionBoxPanel
